import React, { useState, useEffect } from 'react'
import { FaShieldAlt, FaAward, FaExternalLinkAlt } from 'react-icons/fa'
import SEO from '../utils/SEO'

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')

const buildCertificateUrl = (filename) => {
  if (!API_BASE) {
    return `/api/certifications/${filename}`
  }

  if (API_BASE.endsWith('/api')) {
    return `${API_BASE}/certifications/${filename}`
  }

  return `${API_BASE}/api/certifications/${filename}`
}

const CERTIFICATES = [
  {
    id: 'hp-tourism',
    filename: 'hp-tourism.pdf',
    title: 'HP Tourism Travel Agent Registration',
    issuer: 'Govt. of Himachal Pradesh, Dept. of Tourism & Civil Aviation',
    regNumber: 'No. 260226/71088',
    permanentRegNo: '11-1421/2019-DTO-SML-',
    date: '27 Apr 2026',
    validUntil: '26 Apr 2029',
    verifyUrl: 'https://eservices.himachaltourism.gov.in/certificate-validation',
    icon: <FaShieldAlt className="text-blue-600" size={28} />,
    badge: 'Government Registered',
    badgeColor: 'bg-blue-100 text-blue-800',
  },
  {
    id: 'udyam',
    filename: 'udyam.pdf',
    title: 'Udyam MSME Registration',
    issuer: 'Ministry of Micro, Small & Medium Enterprises, Govt. of India',
    regNumber: 'UDYAM-HP-09-0036147',
    permanentRegNo: null,
    date: '20 Feb 2026',
    validUntil: null,
    verifyUrl: null,
    icon: <FaAward className="text-green-600" size={28} />,
    badge: 'MSME Micro Enterprise',
    badgeColor: 'bg-green-100 text-green-800',
  },
]

const Certifications = () => {
  const [active, setActive] = useState(CERTIFICATES[0])
  const [iframeStatus, setIframeStatus] = useState('loading') // 'loading' | 'loaded' | 'error'

  useEffect(() => {
    setIframeStatus('loading')
    // Chrome's PDF viewer doesn't reliably fire onLoad on the host iframe —
    // fall back to 'loaded' after 3 s so the overlay never blocks the PDF.
    const t = setTimeout(() => setIframeStatus((s) => (s === 'loading' ? 'loaded' : s)), 3000)
    return () => clearTimeout(t)
  }, [active.filename])

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Certifications &amp; Registrations | Himachal Yatra"
        description="View Himachal Yatra's official government registrations — HP Tourism Travel Agent Certificate and Udyam MSME Registration — confirming our credentials as a trusted travel agency."
        canonical="/certifications"
        keywords="Himachal Yatra certifications, HP tourism registered travel agent, Udyam MSME registration, verified travel company Himachal"
      />

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <FaShieldAlt className="mx-auto mb-4 text-blue-300" size={48} />
          <h1 className="text-4xl font-bold mb-3">Certifications &amp; Registrations</h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Himachal Yatra is a government-registered travel agency. Our official credentials are
            published here for full transparency.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Selector tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          {CERTIFICATES.map((cert) => (
            <button
              key={cert.id}
              onClick={() => setActive(cert)}
              className={`flex items-center gap-3 px-5 py-3 rounded-xl font-semibold border-2 transition ${
                active.id === cert.id
                  ? 'border-blue-600 bg-blue-50 text-blue-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
              }`}
            >
              {cert.icon}
              <span>{cert.title}</span>
            </button>
          ))}
        </div>

        {/* Certificate detail card */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-100 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-gray-50 rounded-xl">{active.icon}</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{active.title}</h2>
                <p className="text-gray-500 text-sm mt-1">{active.issuer}</p>
              </div>
            </div>
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${active.badgeColor}`}>
              {active.badge}
            </span>
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block mb-1">Registration Number</span>
              <span className="font-semibold text-gray-900">{active.regNumber}</span>
            </div>
            {active.permanentRegNo && (
              <div>
                <span className="text-gray-500 block mb-1">Permanent Registration No.</span>
                <span className="font-semibold text-gray-900">{active.permanentRegNo}</span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block mb-1">
                {active.validUntil ? 'Date Issued' : 'Date of Registration'}
              </span>
              <span className="font-semibold text-gray-900">{active.date}</span>
            </div>
            {active.validUntil && (
              <div>
                <span className="text-gray-500 block mb-1">Valid Until</span>
                <span className="font-semibold text-green-700">{active.validUntil}</span>
              </div>
            )}
            {active.verifyUrl && (
              <div className="sm:col-span-2">
                <span className="text-gray-500 block mb-1">Verification</span>
                <a
                  href={active.verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium"
                >
                  Verify on HP Tourism Portal <FaExternalLinkAlt size={12} />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Embedded PDF viewer */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Certificate Document</h3>
          </div>
          <div className="relative" style={{ height: '75vh', minHeight: '480px' }}>
            {iframeStatus !== 'loaded' && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                {iframeStatus === 'error' ? (
                  <p className="p-6 text-gray-500 text-center">
                    Certificate could not be loaded. Please try again later.
                  </p>
                ) : (
                  <p className="text-gray-400">Loading certificate…</p>
                )}
              </div>
            )}
            <iframe
              key={active.filename}
              src={buildCertificateUrl(active.filename)}
              title={`${active.title} — Official Certificate`}
              className="w-full h-full"
              onLoad={() => setIframeStatus('loaded')}
              onError={() => setIframeStatus('error')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Certifications
